import React from "react"

import type { CompareByDimension } from "@/metrics/compare-by"
import { Tooltip } from "@coval/ui/tooltip"

import type { RunMetricOutput } from "@/types/RunMetricOutput"
import { formatNumber } from "@/utils/formatting/format-number"

import {
  aggregateMetricValues as aggregateValues,
  type GroupedAggregationMode,
  type GroupedAggregationStrategies,
} from "../record-data"

export {
  getAverageValue,
  getMaxValue,
  getMedianValue,
  getMinValue,
  getNumericValuesForAggregation,
  getP95Value,
  type GroupedAggregationMode,
} from "../record-data"

export interface GroupedSimulationCardOptions {
  dimension: CompareByDimension
  metadataKey?: string
  groupKey: string
  metricOutputs: RunMetricOutput[]
  mode?: GroupedAggregationMode
}

interface GroupedSimulationCardContext {
  aggregateUnits: string
  unit: string
  strategies: GroupedAggregationStrategies
}

function getGroupLabel(options: GroupedSimulationCardOptions): string {
  if (options.dimension === "metadata" && options.metadataKey) {
    return `${options.metadataKey} = ${options.groupKey}`
  }
  if (options.dimension === "agent") {
    return `Agent ${options.groupKey}`
  }
  if (options.dimension === "persona") {
    return `Persona ${options.groupKey}`
  }
  if (options.dimension === "test_case") {
    return `Test case ${options.groupKey}`
  }
  return options.groupKey
}

function getModeLabel(mode: GroupedAggregationMode): string {
  if (mode === "p95") {
    return "P95"
  }
  if (mode === "median") {
    return "Median"
  }
  if (mode === "min") {
    return "Min"
  }
  if (mode === "max") {
    return "Max"
  }
  return "Average"
}

export function renderGroupedSimulationCard(
  options: GroupedSimulationCardOptions,
  context: GroupedSimulationCardContext,
): React.ReactNode {
  if (!options.metricOutputs.length) {
    return <div className="text-coval-text-muted text-xs">N/A</div>
  }

  const numericValues = context.strategies.getNumericValues(
    options.metricOutputs,
  )
  if (!numericValues.length) {
    return <div className="text-coval-text-muted text-xs">—</div>
  }

  const mode = options.mode ?? "avg"
  const aggregate = aggregateValues(numericValues, mode, context.strategies)
  const displayValue =
    aggregate !== null && Number.isFinite(aggregate)
      ? formatNumber(aggregate)
      : `${numericValues.length}`
  const unitSuffix = context.aggregateUnits || context.unit
  const chipText = `${displayValue}${unitSuffix ? ` ${unitSuffix}` : ""}`
  const tooltip = `${getGroupLabel(options)} — ${getModeLabel(mode)}: ${chipText}`

  return (
    <Tooltip content={tooltip}>
      <span className="inline-flex items-center rounded-sm min-h-[24px] px-2 max-w-[120px] bg-surface-1 truncate text-xs">
        {chipText}
      </span>
    </Tooltip>
  )
}
